import { z } from 'zod';
import { ALLOWLIST_MIN_PREFIX, AllowlistRange, IpAddress, IsoTime, ServerId } from './basics.js';
import { COMPLIANCE_FIXES_MAX_PER_COMMAND, ComplianceFixId, ComplianceRestart, } from './compliance-fixes.js';
import { CollectionName, FimPath, FimPathString, FimScanIntervalSeconds } from './settings.js';
/**
 * The complete list of things that may run on a server (knowledge doc section 12).
 * The helper rejects anything not in its registry, which is what stops a compromised
 * dashboard or agent from running arbitrary commands (rule 3).
 */
export const OperationName = z.enum([
    'ban_ip',
    'unban_ip',
    'install_crowdsec_collection',
    'reload_crowdsec',
    'restart_crowdsec',
    'set_allowlist',
    // Section 12's "rescan files now", "accept a file change" and "restart Wazuh"
    // are not operations: Wazuh 4.14.8's manager has no on-demand scan, moves its
    // baseline itself, and reports nothing from the first scan after a restart
    // (P7.3). A change is accepted by acknowledging its event, and the schedule is
    // `wazuh_fim.scan_interval_seconds`.
    'set_fim_paths',
    'top_folders_scan',
    'inspect_web_config',
    'set_web_integration',
    'set_web_block_rules',
    'set_web_rate_limits',
    /** P11.5: behaviour rules as CrowdSec scenarios. */
    'set_crowdsec_web_rules',
    /** P11.16: what CrowdSec does when a scenario fires, as profiles. */
    'set_crowdsec_triggers',
    /** P11.6: the fleet blocklist in this server's CrowdSec. */
    'sync_fleet_blocklist',
    /** P11.9.1: CrowdSec's bouncers, hub and metrics address; read-only, no arguments. */
    'inspect_crowdsec',
    /** P11.9.2: remove the collections Bartizo installed and the settings no longer name. */
    'prune_crowdsec_collections',
    /** P11.9.2: which scenarios CrowdSec runs in simulation mode, of those Bartizo manages. */
    'set_crowdsec_simulation',
    /** P11.9.2: an action — the hub's index fetched again and its items upgraded. */
    'upgrade_crowdsec_hub',
    /** P11.9.3–4: CrowdSec's Nginx bouncer and its AppSec firewall, on or off. */
    'set_web_bouncer',
    /** P11.8.2: this server's Wazuh agent enrolled to the central manager, and started. */
    'enrol_wazuh_agent',
    /** P11.8.2: the Wazuh agent's state, enrolment and remote-command locks; read-only. */
    'inspect_wazuh_agent',
    /** P11.19: the catalogue's fixes for failing compliance checks, named by id. */
    'apply_compliance_fixes',
]);
/**
 * Section 12 caps ban duration at 30 days; permanent bans are an admin-only path.
 * `captcha` is accepted only where a challenge-capable bouncer exists, which the
 * API checks against the server's reported capabilities.
 */
export const BanIpArgs = z.object({
    ip: IpAddress,
    duration_seconds: z
        .number()
        .int()
        .min(60)
        .max(30 * 24 * 3600),
    /**
     * Stored by CrowdSec as the decision's scenario and printed by `cscli decisions
     * list`, so no control characters: a newline here would forge a line in that
     * listing, and in every log that quotes it.
     */
    reason: z
        .string()
        .min(1)
        .max(200)
        .regex(/^[^\p{Cc}]+$/u, 'must not contain control characters'),
    decision_type: z.enum(['ban', 'captcha']).default('ban'),
});
export const UnbanIpArgs = z.object({ ip: IpAddress });
export const SetFimPathsArgs = z.object({
    paths: z.array(FimPath).max(200),
    ignore: z.array(FimPathString).max(200),
    /** Absent leaves Wazuh's own schedule alone. */
    scan_interval_seconds: FimScanIntervalSeconds.optional(),
    /** Only say whether the block differs; write nothing (P8.3's alert-only drift). */
    dry_run: z.boolean().optional(),
});
/**
 * The CrowdSec allowlists Bartizo manages, and no others.
 *
 * `bartizo` is the permanent allowlist from the settings (section 11);
 * `bartizo-proxies` holds the confirmed proxy and CDN ranges (rule 16, P5.5). An
 * administrator's own allowlists are theirs: an operation that could name any list
 * could empty one it never made.
 */
export const AllowlistName = z.enum(['bartizo', 'bartizo-proxies']);
/**
 * How much one managed allowlist may exempt in total: sixteen of the widest ranges
 * allowed, per family. The per-entry floor alone would let a thousand /8s through —
 * all of IPv4 — and the helper's only caller is the agent, so this is what stops a
 * compromised agent switching CrowdSec off for the whole internet one entry at a
 * time. Overlaps are counted twice, which errs on the side of refusing. Cloudflare's
 * published IPv4 ranges are under two million addresses; sixteen /8s is 268 million.
 */
export const ALLOWLIST_MAX_WIDEST = 16;
export function withinAllowlistCoverage(ranges) {
    let v4 = 0n;
    let v6 = 0n;
    for (const range of ranges) {
        const slash = range.lastIndexOf('/');
        const prefix = BigInt(slash === -1 ? (range.includes(':') ? 128 : 32) : Number(range.slice(slash + 1)));
        if (range.includes(':'))
            v6 += 1n << (128n - prefix);
        else
            v4 += 1n << (32n - prefix);
    }
    const widest = BigInt(ALLOWLIST_MAX_WIDEST);
    return (v4 <= widest << BigInt(32 - ALLOWLIST_MIN_PREFIX.ipv4) &&
        v6 <= widest << BigInt(128 - ALLOWLIST_MIN_PREFIX.ipv6));
}
/**
 * The whole list, not a change to it: the helper makes the allowlist equal to
 * `ranges`, so repeating it changes nothing (rule 8) and a list somebody edited by
 * hand is put back. A thousand entries is two of section 11's permanent allowlists
 * with room for every published CDN range.
 */
export const SetAllowlistArgs = z
    .object({
    name: AllowlistName,
    ranges: z.array(AllowlistRange).max(1000),
})
    .refine(({ ranges }) => withinAllowlistCoverage(ranges), {
    message: `together the ranges must exempt no more than ${String(ALLOWLIST_MAX_WIDEST)} /${String(ALLOWLIST_MIN_PREFIX.ipv4)}s of IPv4 or /${String(ALLOWLIST_MIN_PREFIX.ipv6)}s of IPv6`,
    path: ['ranges'],
});
export const InstallCrowdsecCollectionArgs = z.object({ collection: CollectionName });
/**
 * `prune_crowdsec_collections` (P11.9.2, C4): the collections the settings name.
 * The helper removes those **it** installed (its own record, kept as root) that
 * are not listed — nothing else, whatever the agent asks — so a compromised agent
 * cannot strip the installer's `crowdsecurity/linux` from a server.
 */
export const PruneCrowdsecCollectionsArgs = z
    .object({ keep: z.array(CollectionName).max(100) })
    .strict();
/**
 * `set_crowdsec_simulation` (P11.9.2, C3): the scenarios and collections to watch,
 * the whole list. The helper expands a collection into its scenarios, puts those in
 * simulation, and takes out of simulation only what it put there earlier — a
 * scenario a person put in simulation with `cscli` stays theirs.
 */
export const SetCrowdsecSimulationArgs = z
    .object({
    names: z
        .array(CollectionName.refine((name) => !name.toLowerCase().startsWith('bartizo/'), {
        message: "Bartizo's own scenarios are watched from their block rule",
    }))
        .max(100),
})
    .strict();
/** What `set_crowdsec_simulation` did, in the outcome's `data`. */
export const CrowdsecSimulationResult = z.object({
    /** Every scenario Bartizo now keeps in simulation. */
    simulated: z.array(z.string().max(128)).max(1000),
    /** Names that are neither an installed collection nor an installed scenario. */
    unknown: z.array(z.string().max(128)).max(100),
    changed: z.boolean(),
});
/**
 * `upgrade_crowdsec_hub` (P11.9.2, C4): `cscli hub update`, then `cscli hub
 * upgrade` — which leaves tainted items alone — then a reload. Nothing to choose.
 */
export const UpgradeCrowdsecHubArgs = z.object({}).strict();
/**
 * `apply_compliance_fixes` (P11.19): the fixes in `COMPLIANCE_FIXES`, by id and in
 * the order given. An id is the only thing a caller chooses; the commands behind
 * it are fixed at build time (rule 3), so there is no text here that is ever run.
 */
export const ApplyComplianceFixesArgs = z
    .object({
    fixes: z
        .array(ComplianceFixId)
        .min(1)
        .max(COMPLIANCE_FIXES_MAX_PER_COMMAND)
        .refine((ids) => new Set(ids).size === ids.length, { message: 'each fix once' }),
})
    .strict();
/**
 * What one fix came to. `skipped` is a fix the helper chose not to make because a
 * person should decide (removing the package would take others with it); nothing is
 * broken, and the check stays failing until they do.
 */
export const ComplianceFixStatus = z.enum(['applied', 'skipped', 'failed']);
/** What `apply_compliance_fixes` did, in the outcome's `data`. */
export const ApplyComplianceFixesResult = z.object({
    fixes: z
        .array(z.object({
        id: ComplianceFixId,
        status: ComplianceFixStatus,
        /** The end of what the fix printed, for a person; bounded. */
        detail: z.string().max(500).optional(),
        restart: ComplianceRestart,
    }))
        .max(COMPLIANCE_FIXES_MAX_PER_COMMAND),
    /** True when an applied fix needs a reboot before its check will pass. */
    pending_restart: z.boolean(),
    /**
     * Whether the Wazuh agent was restarted so its next configuration assessment
     * runs now rather than within 12 hours: `started`, `unavailable` where there is
     * no Wazuh agent here (or nothing was applied), `failed` when the restart did not
     * work. Absent from a helper that predates it.
     */
    rescan: z.enum(['started', 'unavailable', 'failed']).optional(),
});
/** The captcha services CrowdSec's Nginx bouncer can show (C6). */
export const CaptchaProvider = z.enum(['turnstile', 'hcaptcha', 'recaptcha']);
/**
 * A captcha site or secret key. Written into a `KEY=value` file by the helper, so
 * nothing but the characters the providers use: no space, no newline, no `=`.
 */
export const CaptchaKey = z
    .string()
    .min(10)
    .max(200)
    .regex(/^[A-Za-z0-9_.-]+$/, 'letters, digits, dot, dash and underscore only');
export const CaptchaKeys = z
    .object({ provider: CaptchaProvider, site_key: CaptchaKey, secret_key: CaptchaKey })
    .strict();
/**
 * `set_web_bouncer` (P11.9.3–4, decisions C5–C7): the whole desired state of
 * CrowdSec's Nginx bouncer on this server. On: the one fixed package,
 * `crowdsec-nginx-bouncer`, installed from the CrowdSec repository the installer
 * added if it is not there yet, then switched on; off: switched off, never
 * uninstalled. `captcha` null leaves the challenge unconfigured. Nothing else can
 * be named — no package, no path, no free text (S24).
 */
export const SetWebBouncerArgs = z
    .object({
    enabled: z.boolean(),
    appsec: z.enum(['off', 'watch', 'block']),
    captcha: CaptchaKeys.nullable(),
})
    .strict();
/** What `set_web_bouncer` found and left, in the outcome's `data`. */
export const WebBouncerResult = z.object({
    installed: z.boolean(),
    enabled: z.boolean(),
    appsec: z.enum(['off', 'watch', 'block']),
    challenge: z.boolean(),
    changed: z.boolean(),
});
/** `reload_crowdsec` and `restart_crowdsec` take nothing: there is nothing to choose. */
export const ReloadCrowdsecArgs = z.object({});
export const RestartCrowdsecArgs = z.object({});
/**
 * The operations that may arrive as a **command** (section 12's actions), as
 * against those the agent runs itself to apply its settings (`set_allowlist`,
 * `install_crowdsec_collection`, the web and Wazuh operations). An agent refuses a
 * command naming anything else, so a compromised API cannot use the command queue
 * to rewrite a server's configuration behind its settings' back.
 */
export const COMMAND_OPERATIONS = [
    'ban_ip',
    'unban_ip',
    'upgrade_crowdsec_hub',
    'apply_compliance_fixes',
];
export const isCommandOperation = (name) => COMMAND_OPERATIONS.includes(name);
/**
 * Where a command is (section 16's `commands.status`).
 *
 * `pending` until an agent collects it, `sent` once it has, then one of the four
 * a server reports — or `expired` when the API's own sweep finds it past its
 * expiry, `cancelled` when somebody stopped the action first, and `skipped` for a
 * server in the target that could never have run it.
 */
export const CommandStatus = z.enum([
    'pending',
    'sent',
    'done',
    'failed',
    'rejected',
    'expired',
    'cancelled',
    'skipped',
]);
/** The states nothing moves out of. */
export const FINAL_COMMAND_STATUSES = [
    'done',
    'failed',
    'rejected',
    'expired',
    'cancelled',
    'skipped',
];
/**
 * `args` stays `unknown` here on purpose: the helper's registry validates it against
 * the schema for that specific operation, so a wrong shape cannot reach a handler.
 *
 * Every command carries `expiresAt`, so a server that was offline never runs a stale
 * ban hours later (rule 8).
 */
export const Command = z.object({
    id: z.uuid(),
    serverId: ServerId,
    operation: OperationName,
    args: z.unknown(),
    expiresAt: IsoTime,
    requestedBy: z.string().min(1).max(200),
});
export const CommandResult = z.object({
    commandId: z.uuid(),
    status: z.enum(['done', 'failed', 'expired', 'rejected']),
    detail: z.string().max(1000).optional(),
    finishedAt: IsoTime,
});
/** `POST /v1/agent/commands/:id/result`: `CommandResult` without the id, which is in the path. */
export const CommandResultReport = CommandResult.omit({ commandId: true });
/** What the API now holds for the command. The same answer to a repeated report. */
export const CommandResultReceipt = z.object({
    commandId: z.uuid(),
    status: CommandStatus,
});
//# sourceMappingURL=command.js.map